// Ответ ассистента: разметка модели, приведённая к виду. SMY-115
//
// Правила разбора живут в `markdown.ts` и проверяются тестом; здесь только их
// отрисовка. Граница та же, что у `icons.ts` и `Icon.tsx`.

import type { ReactNode } from 'react'

import { blocksOf, inline, type Token } from '@smyslika/core'

const spans = (text: string): ReactNode[] =>
  inline(text).map((token: Token, i: number) => {
    if (token.kind === 'bold') return <strong key={i}>{token.text}</strong>
    if (token.kind === 'code') return <code key={i}>{token.text}</code>
    return token.text
  })

/** Текст ответа со структурой: заголовки, абзацы, списки, выделения. */
export function Answer({ children }: { children: string }) {
  const blocks = blocksOf(children)
  // Разметки не нашлось — не заворачиваем в лишние теги: короткий ответ в одну
  // строку остаётся одной строкой.
  if (blocks.length === 1 && blocks[0]?.kind === 'text') {
    return <p className="answer__p">{spans(blocks[0].lines.join(' '))}</p>
  }

  return (
    <div className="answer">
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          // Настоящий заголовок, а не жирный абзац: по ним ходит программа
          // чтения с экрана, и длинный ответ с разделами читается ею так же,
          // как глазами.
          //
          // Уровень начинается с третьего: ответ живёт ВНУТРИ страницы, где
          // первый занят её названием, а второй — окнами поверх. Глубже
          // четвёртого не идём — «###» и «####» в одном ответе означают одно
          // и то же «мельче предыдущего», а не шесть ступеней иерархии.
          const Tag = block.level <= 2 ? 'h3' : 'h4'
          return (
            <Tag key={i} className={`answer__head answer__head--${block.level <= 2 ? 'big' : 'small'}`}>
              {spans(block.text)}
            </Tag>
          )
        }
        if (block.kind === 'rule') {
          return <hr key={i} className="answer__rule" />
        }
        if (block.kind === 'bullets') {
          return (
            <ul key={i} className="answer__list">
              {block.items.map((item, j) => (
                <li key={j}>{spans(item)}</li>
              ))}
            </ul>
          )
        }
        if (block.kind === 'numbers') {
          return (
            <ol key={i} className="answer__list">
              {block.items.map((item, j) => (
                <li key={j}>{spans(item)}</li>
              ))}
            </ol>
          )
        }
        return (
          <p key={i} className="answer__p">
            {spans(block.lines.join(' '))}
          </p>
        )
      })}
    </div>
  )
}
